import React, { createContext, useContext, useReducer } from 'react';

import __noop from '@atlaskit/ds-lib/noop';

import type { ColorMode } from '../example/types';
import { getUserPreferencesLocalStorageKey } from './local-storage-key';

export type UserPreferences = {
	/**
	 * Site-wide color mode preference.
	 */
	colorMode: ColorMode | null;
	/**
	 * Code example color mode preference.
	 */
	exampleColorMode: ColorMode | null;
	/**
	 * Sync example user preferences across all examples.
	 */
	exampleSyncSettings: 'true' | 'false' | null;
	/**
	 * Per-flag choices from example Styles form (e.g. labelling Refreshed/Legacy).
	 * Persisted so the choice is restored across examples and page reloads.
	 */
	exampleFeatureFlags: Record<string, boolean> | null;
};

const defaultUserPreferences: UserPreferences = {
	colorMode: null,
	exampleColorMode: null,
	exampleSyncSettings: null,
	exampleFeatureFlags: null,
};

type UserPreferencesContextProps = {
	userPreferences: UserPreferences;
	setUserPreferences: (preferences: Partial<UserPreferences>) => void;
};

const UserPreferencesContext = createContext<UserPreferencesContextProps>({
	userPreferences: defaultUserPreferences,
	setUserPreferences: __noop,
});

export const useUserPreferences: () => {
	userPreferences: UserPreferences;
	setUserPreferences: (preferences: Partial<UserPreferences>) => void;
} = () => {
	const { userPreferences, setUserPreferences } = useContext(UserPreferencesContext);

	return { userPreferences, setUserPreferences };
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export { getUserPreferencesLocalStorageKey };

/**
 * Reads user preferences from local storage and returns the initial state
 * for initial render.
 */
const EXAMPLE_FEATURE_FLAGS_KEY: keyof UserPreferences = 'exampleFeatureFlags';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getInitialUserPreferences: () => UserPreferences = () => {
	let initialUserPreferences: UserPreferences = { ...defaultUserPreferences };

	// SSR-safe check
	if (typeof window !== 'undefined') {
		Object.keys(initialUserPreferences).forEach((key) => {
			const prefKey = key as keyof UserPreferences;
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			const storedValue = localStorage.getItem(getUserPreferencesLocalStorageKey(key));
			if (storedValue !== null) {
				if (prefKey === EXAMPLE_FEATURE_FLAGS_KEY) {
					try {
						initialUserPreferences[prefKey] = JSON.parse(storedValue) as Record<string, boolean>;
					} catch {
						// ignore invalid JSON
					}
				} else {
					// Other preferences are stored as plain strings (colorMode, exampleColorMode, exampleSyncSettings)
					(initialUserPreferences as Record<string, string | null>)[prefKey] = storedValue;
				}
			}
		});
	}

	return initialUserPreferences;
};

type UserPreferencesProviderProps = {
	children?: React.ReactNode;
};

type Action = {
	type: 'SET_USER_PREFERENCES';
	payload: Partial<UserPreferences>;
};

const userPreferenceReducer = (state: UserPreferences, action: Action): UserPreferences => {
	switch (action.type) {
		case 'SET_USER_PREFERENCES': {
			const newState = { ...state, ...action.payload };
			Object.entries(action.payload).forEach(([preferenceName, value]) => {
				const storageKey = getUserPreferencesLocalStorageKey(preferenceName);
				if (value === null) {
					// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
					localStorage.removeItem(storageKey);
				} else if (preferenceName === EXAMPLE_FEATURE_FLAGS_KEY) {
					// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
					localStorage.setItem(storageKey, JSON.stringify(value));
				} else {
					// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
					localStorage.setItem(storageKey, value.toString());
				}
			});
			return newState;
		}
		default:
			return state;
	}
};

/**
 * __User preferences provider__
 *
 * Provides access to user preferences which are stored within, and accessed from local storage.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const UserPreferencesProvider: ({
	children,
}: UserPreferencesProviderProps) => React.JSX.Element = ({
	children,
}: UserPreferencesProviderProps): React.JSX.Element => {
	const [userPreferences, dispatch] = useReducer(
		userPreferenceReducer,
		{},
		getInitialUserPreferences,
	);

	const setUserPreferences = (preferences: Partial<UserPreferences>) => {
		dispatch({ type: 'SET_USER_PREFERENCES', payload: preferences });
	};

	return (
		<UserPreferencesContext.Provider
			value={{
				userPreferences,
				setUserPreferences,
			}}
		>
			{children}
		</UserPreferencesContext.Provider>
	);
};
